import { useSession } from "@internal/auth";
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
} from "react-router-dom";
import { UpdateBanner } from "@/components/shared/UpdateBanner";
import { useObservable } from "@/hooks/useObservable";
import { useProfile } from "@/hooks/useProfile";
import { AuthGuard } from "@/router/AuthGuard";
import { RoleGuard } from "@/router/RoleGuard";
import { ChildConfirmWeek } from "@/screens/child/ChildConfirmWeek";
import { ChildHistory } from "@/screens/child/ChildHistory";
import { ChildHome } from "@/screens/child/ChildHome";
import { ChildWeek } from "@/screens/child/ChildWeek";
import { ParentDashboard } from "@/screens/parent/ParentDashboard";
import { ParentFamily } from "@/screens/parent/ParentFamily";
import { ParentJobs } from "@/screens/parent/ParentJobs";
import { ParentRewards } from "@/screens/parent/ParentRewards";
import { ParentWeekDetail } from "@/screens/parent/ParentWeekDetail";
import { ParentWeeks } from "@/screens/parent/ParentWeeks";
import { SettingsScreen } from "@/screens/Settings";
import { familyService } from "@/services/family.service";

function child(element: React.ReactNode) {
  return (
    <AuthGuard>
      <RoleGuard role="child">{element}</RoleGuard>
    </AuthGuard>
  );
}

function parent(element: React.ReactNode) {
  return (
    <AuthGuard>
      <RoleGuard role="parent">{element}</RoleGuard>
    </AuthGuard>
  );
}

const router = createBrowserRouter([
  { path: "/child", element: child(<ChildHome />) },
  { path: "/child/week", element: child(<ChildWeek />) },
  { path: "/child/confirm", element: child(<ChildConfirmWeek />) },
  { path: "/child/history", element: child(<ChildHistory />) },

  { path: "/parent/dashboard", element: parent(<ParentDashboard />) },
  { path: "/parent/jobs", element: parent(<ParentJobs />) },
  { path: "/parent/rewards", element: parent(<ParentRewards />) },
  { path: "/parent/weeks", element: parent(<ParentWeeks />) },
  { path: "/parent/weeks/:yearWeek", element: parent(<ParentWeekDetail />) },
  { path: "/parent/family", element: parent(<ParentFamily />) },

  { path: "/parent", element: <Navigate to="/parent/dashboard" replace /> },
  {
    path: "/settings",
    element: (
      <AuthGuard>
        <SettingsScreen />
      </AuthGuard>
    ),
  },
  { path: "/", element: <RootRedirect /> },
  { path: "*", element: <Navigate to="/" replace /> },
]);

export function App() {
  const { loading: sessionLoading, user } = useSession();
  const { loading: profileLoading } = useProfile();
  const { loading: familyLoading } = useObservable(familyService.state$, {
    members: [],
    settings: null,
    loading: false,
  });

  const authLoading = sessionLoading || (user !== null && profileLoading);
  if (authLoading || (user && familyLoading)) {
    return (
      <>
        <UpdateBanner />
        <div
          style={{
            width: "100vw",
            height: "100dvh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "var(--bg)",
          }}
        >
          <div
            style={{
              fontFamily: "var(--font-display)",
              fontStyle: "italic",
              fontSize: 22,
              color: "var(--ink-3)",
            }}
          >
            Laster…
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <UpdateBanner />
      <RouterProvider router={router} />
    </>
  );
}

function RootRedirect() {
  const { profile } = useProfile();
  if (profile?.data.role === "parent")
    return <Navigate to="/parent/dashboard" replace />;
  return <Navigate to="/child" replace />;
}
